/**
 * Iframe Resize Detection Utility
 * 
 * Detects when Passport content size changes and notifies parent frame
 * via postMessage. Only active when embedded in an iframe.
 * 
 * Based on Veracast implementation by Andrew Claasen
 */

export interface ResizeMessage {
  width: number;
  height: number;
  event: 'frameresize';
}

export class IframeResizeDetectorService {
  private resizeObserver: ResizeObserver | null = null;
  private iframeFallback: HTMLIFrameElement | null = null;
  private sizeDetector: HTMLDivElement | null = null;
  private bouncer: number | null = null;
  private isEmbedded: boolean;

  constructor() {
    this.isEmbedded = window.self !== window.top;
  }

  /**
   * Initialize resize detection
   */
  public init(): void {
    if (!this.isEmbedded) {
      return; // Only activate when embedded in iframe
    }

    this.setupResizeObserver();
    this.setupIframeFallback();
  }

  /**
   * Cleanup resize detection
   */
  public destroy(): void {
    if (this.resizeObserver && this.sizeDetector) {
      this.resizeObserver.unobserve(this.sizeDetector);
      this.resizeObserver.disconnect();
    }

    if (this.iframeFallback) {
      this.iframeFallback.removeEventListener('resize', this.resizeHandler);
    }

    if (this.bouncer) {
      clearTimeout(this.bouncer);
    }
  }

  /**
   * Setup ResizeObserver for modern browsers
   */
  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return; // Fallback to iframe method for older browsers
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeHandler();
    });
  }

  /**
   * Setup iframe fallback for older browsers
   */
  private setupIframeFallback(): void {
    // Iframe fallback will be created by the React component
    // This method is called to prepare for iframe event listener
  }

  /**
   * Attach observer to size detector element
   */
  public attachObserver(sizeDetector: HTMLDivElement): void {
    if (this.resizeObserver && sizeDetector) {
      this.sizeDetector = sizeDetector;
      this.resizeObserver.observe(sizeDetector);
    }
  }

  /**
   * Attach iframe fallback event listener
   */
  public attachIframeFallback(iframe: HTMLIFrameElement): void {
    if (iframe) {
      this.iframeFallback = iframe;
      this.iframeFallback.addEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * Get the height based on 16:9 aspect ratio using current window width
   */
  private getDocumentHeight(): number {
    // Calculate height based on 16:9 aspect ratio
    return window.innerWidth * (9 / 16);
  }

  /**
   * Handle resize events with debouncing (exactly like Veracast)
   */
  public resizeHandler = (): void => {
    const size: ResizeMessage = {
      width: window.innerWidth,
      height: this.getDocumentHeight(),
      event: 'frameresize'
    };

    if (this.bouncer) {
      clearTimeout(this.bouncer);
    }

    this.bouncer = window.setTimeout(() => {
      // Only fire postMessage if parent frame supports it
      if (window.parent && window.parent.postMessage) {
        // eslint-disable-next-line no-console
        console.log('Sending resize message:', size);
        window.parent.postMessage(JSON.stringify(size), '*');
      }
    }, 200);
  };

  /**
   * Check if currently embedded in iframe
   */
  public getIsEmbedded(): boolean {
    return this.isEmbedded;
  }
}